import { trackDownload } from '../utils/downloadIndicator';
import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toolsApi } from '../services/api';

const acceptedFileTypes = 'image/png,image/jpeg,image/gif,image/bmp,image/tiff,application/pdf';
const maxUploadSizeBytes = 4 * 1024 * 1024;
const maxUploadSizeLabel = '4 MB';
const oversizedFileMessage = `Files must be smaller than ${maxUploadSizeLabel}. For large PDF files, take screenshots of the table pages and upload those images instead.`;

const escapeDelimitedValue = (value) => {
  const text = value === null || typeof value === 'undefined' ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const buildTsv = (items) => {
  const lines = [['Description', 'Quantity']];
  items.forEach((item) => {
    lines.push([item.description || '', item.quantity ?? '']);
  });
  return lines.map((row) => row.join('\t')).join('\n');
};

const buildCsv = (items) => {
  const lines = [['Description', 'Quantity']];
  items.forEach((item) => {
    lines.push([item.description || '', item.quantity ?? '']);
  });
  return lines.map((row) => row.map(escapeDelimitedValue).join(',')).join('\n');
};

function PlantScheduleTool() {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [uploadWarning, setUploadWarning] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const items = useMemo(() => result?.items || [], [result?.items]);
  const analyzedFile = result?.analyzed_file || null;

  const totalQuantity = useMemo(() => (
    items.reduce((sum, item) => sum + (Number.isFinite(Number(item.quantity)) ? Number(item.quantity) : 0), 0)
  ), [items]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (file && file.size >= maxUploadSizeBytes) {
      setSelectedFile(null);
      setResult(null);
      setError('');
      setUploadWarning(`${file.name} is too large. ${oversizedFileMessage}`);
      setCopyStatus('');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    setResult(null);
    setError('');
    setUploadWarning('');
    setCopyStatus('');
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleExtract = async () => {
    if (!selectedFile) {
      setError('Choose a plant schedule PDF or image first.');
      return;
    }

    if (selectedFile.size >= maxUploadSizeBytes) {
      setError('');
      setUploadWarning(`${selectedFile.name} is too large. ${oversizedFileMessage}`);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setLoading(true);
    setError('');
    setUploadWarning('');
    setCopyStatus('');

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await toolsApi.extractPlantSchedule(formData);
      setResult(response.data);
    } catch (extractError) {
      const message = extractError.response?.data?.error || 'Plant schedule extraction failed.';
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!items.length) return;

    try {
      await navigator.clipboard.writeText(buildTsv(items));
      setCopyStatus('Copied results to clipboard.');
      setTimeout(() => setCopyStatus(''), 2500);
    } catch {
      setCopyStatus('Clipboard access was blocked by the browser.');
    }
  };

  const handleDownload = trackDownload(() => {
    if (!items.length) return;

    const blob = new Blob([buildCsv(items)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'plant-schedule.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, { defer: true });

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} lg={4}>
        <Paper sx={{ p: 2.5, height: '100%' }}>
          <Stack spacing={2.25}>
            <Box>
              <Typography variant="h6">Plant Schedule Extractor</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Upload a schedule table and convert it into quote-ready plant descriptions.
              </Typography>
            </Box>

            <Box
              onClick={handleChooseFile}
              sx={{
                border: '1px dashed',
                borderColor: selectedFile ? 'primary.main' : 'divider',
                bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(0,135,193,0.04)' : 'rgba(0,135,193,0.1)',
                borderRadius: 2,
                cursor: 'pointer',
                p: 2,
                minHeight: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFileTypes}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <Stack spacing={1.25} alignItems="center">
                <UploadFileIcon color="primary" sx={{ fontSize: 38 }} />
                <Box>
                  <Typography variant="subtitle2">
                    {selectedFile ? selectedFile.name : 'Choose PDF or image'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, TIFF, BMP, GIF, or PDF under {maxUploadSizeLabel}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Alert severity="info" variant="outlined">
              Large PDF over 4mb? Take screenshots of the table pages and upload those images instead.
            </Alert>

            {selectedFile && (
              <Stack direction="row" spacing={1} alignItems="center">
                <InsertDriveFileIcon fontSize="small" color="action" />
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                  {selectedFile.name}
                </Typography>
                <Chip
                  size="small"
                  label={`${Math.max(selectedFile.size / 1024 / 1024, 0.01).toFixed(2)} MB`}
                  variant="outlined"
                />
              </Stack>
            )}

            <Button
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleExtract}
              disabled={loading || !selectedFile}
              fullWidth
            >
              {loading ? 'Extracting' : 'Extract Schedule'}
            </Button>

            <Divider />

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="h5">{items.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Rows</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="h5">{totalQuantity}</Typography>
                  <Typography variant="caption" color="text.secondary">Quantity</Typography>
                </Box>
              </Grid>
            </Grid>

            {analyzedFile && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" label={`${analyzedFile.pages_analyzed || 0} page(s)`} />
                <Chip size="small" label={`${analyzedFile.tables_detected || 0} table(s)`} />
              </Stack>
            )}
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} lg={8}>
        <Paper sx={{ height: '100%', minHeight: 520, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">Extracted Schedule</Typography>
              <Typography variant="body2" color="text.secondary">
                Review descriptions and quantities before copying into your workflow.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={!items.length}
                onClick={handleCopy}
              >
                Copy
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={!items.length}
                onClick={handleDownload}
              >
                CSV
              </Button>
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {uploadWarning && <Alert severity="warning" icon={<WarningAmberIcon />}>{uploadWarning}</Alert>}
            {copyStatus && <Alert severity={copyStatus.includes('Copied') ? 'success' : 'warning'}>{copyStatus}</Alert>}
            {result?.warnings?.map((warning) => (
              <Alert key={warning} severity="warning" icon={<WarningAmberIcon />}>{warning}</Alert>
            ))}

            {items.length > 0 ? (
              <TableContainer sx={{ flex: 1 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 72 }}>Qty</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ width: 130 }}>Source</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={`${item.description}-${index}`} hover>
                        <TableCell>{item.quantity ?? '-'}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {item.source_page ? `Page ${item.source_page}` : 'Document'}
                            {item.source_table ? `, table ${item.source_table}` : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 320,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  p: 3,
                }}
              >
                <Stack spacing={1} alignItems="center">
                  <TableChartIcon color="disabled" sx={{ fontSize: 46 }} />
                  <Typography variant="subtitle1">No extracted rows yet</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                    Results will appear here after the plant schedule has been analysed.
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

function Tools() {
  const [activeTab, setActiveTab] = useState('plant-schedule');

  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1 }}
        >
          <Tab value="plant-schedule" label="Plant Schedule" />
        </Tabs>
      </Paper>

      {activeTab === 'plant-schedule' && <PlantScheduleTool />}
    </Box>
  );
}

export default Tools;
